import React, { useState, useEffect } from 'react';
import { ArrowDown, ArrowUp, Pencil, User, Calendar, FileText, Package } from 'lucide-react';
import { Transaction } from '../../types';
import { useInventory } from '../../context/InventoryContext';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import Badge from '../ui/Badge';
import StickyScrollContainer from '../ui/StickyScrollContainer';

interface TransactionListProps {
  transactions: Transaction[];
}

const TransactionList: React.FC<TransactionListProps> = ({ transactions }) => {
  const { products } = useInventory();
  const { user } = useAuth();
  const [poNumberCache, setPoNumberCache] = useState<Record<string, string>>({});

  // Load PO numbers for any UUIDs in metadata
  useEffect(() => {
    const loadMissingPoNumbers = async () => {
      const uuidPoIds = new Set<string>();
      
      // Find all purchase_order_id values that look like UUIDs
      transactions.forEach(transaction => {
        const metadata = transaction.metadata || {};
        const poId = metadata.purchase_order_id;
        
        if (poId && 
            typeof poId === 'string' && 
            poId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i) &&
            !poNumberCache[poId]) {
          uuidPoIds.add(poId);
        }
      });
      
      if (uuidPoIds.size > 0) {
        try {
          const { data: purchaseOrders, error } = await supabase
            .from('purchase_orders')
            .select('id, order_number')
            .in('id', Array.from(uuidPoIds));
          
          if (error) throw error;
          
          const newCache: Record<string, string> = {};
          purchaseOrders?.forEach(po => {
            newCache[po.id] = po.order_number;
          });
          
          setPoNumberCache(prev => ({ ...prev, ...newCache }));
        } catch (error) {
          console.error('Error loading PO numbers:', error);
        }
      }
    };
    
    loadMissingPoNumbers();
  }, [transactions, poNumberCache]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'in':
        return <ArrowDown className="h-4 w-4 text-green-600" />;
      case 'out':
        return <ArrowUp className="h-4 w-4 text-red-600" />;
      case 'adjustment':
        return <Pencil className="h-4 w-4 text-amber-600" />;
      default:
        return null;
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    switch (type) {
      case 'in':
        return <Badge variant="success">Stock In</Badge>;
      case 'out':
        return <Badge variant="danger">Stock Out</Badge>;
      case 'adjustment':
        return <Badge variant="warning">Adjustment</Badge>;
      default:
        return null;
    }
  };

  const getProductDetails = (productId: string) => {
    const product = products.find(p => p.id === productId);
    return {
      name: product?.name || 'Product Not Found',
      sku: product?.sku || 'N/A'
    };
  };

  if (transactions.length === 0) {
    return <p className="text-gray-500 text-center py-6">No transactions found.</p>;
  }

  return (
    <StickyScrollContainer>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Date & Time
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Product
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Type
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Quantity
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              User
            </th>
            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Notes
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {transactions.map((transaction) => {
            const product = getProductDetails(transaction.product_id);
            const isCurrentUser = user?.id === transaction.user_id;
            const metadata = transaction.metadata || {};
            
            return (
              <tr key={transaction.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 text-gray-600 mr-2" />
                    {formatDate(transaction.date)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{product.name}</div>
                  <div className="text-sm text-gray-500">SKU: {product.sku}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div className="flex items-center">
                    {getTransactionIcon(transaction.type)}
                    <span className="ml-2">{getTransactionTypeLabel(transaction.type)}</span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <span className={`font-medium ${
                    transaction.type === 'in' 
                      ? 'text-green-600' 
                      : transaction.type === 'out' 
                        ? 'text-red-600' 
                        : 'text-amber-600'
                  }`}>
                    {transaction.type === 'in' ? '+' : transaction.type === 'out' ? '-' : ''}
                    {transaction.quantity}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <Badge
                    variant={
                      transaction.status === 'completed' ? 'success' :
                      transaction.status === 'pending' ? 'warning' :
                      'danger'
                    }
                  >
                    {transaction.status}
                  </Badge>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-gray-600 mr-2" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {transaction.user_reference || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500">
                        {isCurrentUser && '(You)'}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {(() => {
                    const metadata = transaction.metadata || {};
                    const notes = transaction.notes || '';
                    
                    // Get PO number from metadata first (most reliable)
                    let poNumber = metadata.purchase_order_number || null;
                    
                    // If metadata has a UUID for purchase_order_id, try to get the actual order number
                    if (!poNumber && metadata.purchase_order_id) {
                      const poId = metadata.purchase_order_id;
                      if (typeof poId === 'string' && poId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
                        poNumber = poNumberCache[poId] || null;
                      } else {
                        poNumber = poId; // It's already an order number
                      }
                    }
                    
                    // If no PO number in metadata, try to extract from notes as fallback
                    if (!poNumber && notes) {
                      // Look for various PO patterns in notes
                      const poPatterns = [
                        /Received from\s+(PO-[A-Z0-9-]+)/i,
                        /PO:\s*(PO-[A-Z0-9-]+)/i,
                        /Purchase Order:\s*(PO-[A-Z0-9-]+)/i,
                        /from PO:\s*(PO-[A-Z0-9-]+)/i,
                        /(PO-\d{6}-\d{3})/i
                      ];
                      
                      for (const pattern of poPatterns) {
                        const match = notes.match(pattern);
                        if (match && match[1]) {
                          poNumber = match[1];
                          break;
                        }
                      }
                    }
                    
                    // Clean up notes to remove redundant PO information
                    let cleanNotes = notes;
                    if (cleanNotes && poNumber) {
                      // Remove PO references from notes since we display them separately
                      cleanNotes = cleanNotes
                        .replace(new RegExp(`Received from\\s+${poNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
                        .replace(new RegExp(`from\\s+${poNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
                        .replace(new RegExp(`PO:\\s*${poNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
                        .replace(new RegExp(`Purchase Order:\\s*${poNumber.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'gi'), '')
                        .replace(/Received from PO:\s*[^\n]*/gi, '')
                        .replace(/Received from\s+PO-[A-Z0-9-]+/gi, '')
                        .replace(/PO:\s*[^\n]*/gi, '')
                        .replace(/Purchase Order:\s*[^\n]*/gi, '')
                        .replace(/from PO:\s*[^\n]*/gi, '')
                        .trim();
                    }
                    
                    // Remove common placeholder text
                    if (cleanNotes === 'test' || cleanNotes === 'Received items from purchase order' || cleanNotes.trim() === '') {
                      cleanNotes = '';
                    }
                    
                    // Check if we have anything meaningful to display
                    const hasContent = poNumber || metadata.condition || metadata.lot_number || cleanNotes;
                    
                    if (!hasContent) {
                      return <span>-</span>;
                    }
                    
                    return (
                      <div className="space-y-1">
                        {/* Purchase Order Number */}
                        {poNumber && (
                          <div className="flex items-center text-sm">
                            <FileText className="h-3 w-3 text-blue-600 mr-1" />
                            <span className="font-medium text-blue-900">{poNumber}</span>
                          </div>
                        )}
                        
                        {/* Supplier Information */}
                        {metadata.supplier_name && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Supplier:</span>
                            <span className="ml-1">{metadata.supplier_name}</span>
                          </div>
                        )}
                        
                        {/* Condition */}
                        {metadata.condition && (
                          <div className="flex items-center text-sm text-gray-700">
                            <Package className="h-3 w-3 text-gray-500 mr-1" />
                            <span className="font-medium">Condition:</span>
                            <span className="ml-1">{metadata.condition}</span>
                          </div>
                        )}
                        
                        {/* Lot Number */}
                        {metadata.lot_number && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Lot:</span>
                            <span className="ml-1">{metadata.lot_number}</span>
                          </div>
                        )}
                        
                        {/* User Notes */}
                        {cleanNotes && (
                          <div className="text-sm text-gray-700">
                            <span className="font-medium">Notes:</span>
                            <span className="ml-1">{cleanNotes}</span>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </StickyScrollContainer>
  );
};

export default TransactionList;